import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import type { Role } from '../lib/roles'

/** A member's role in a company, as the backend stores it. */
export type { Role }

/** The filter of screen 10, as `GET /api/activity` takes it in `kind`. */
export type ActivityKind = 'all' | 'invitations' | 'roles' | 'removals' | 'sharing' | 'company'

export const ACTIVITY_EVENTS = [
  'invited',
  'invite_resent',
  'invite_revoked',
  'invite_auto_revoked',
  'invite_accepted',
  'invite_declined',
  'role_changed',
  'member_removed',
  'member_left',
  'share_link_created',
  'share_link_revoked',
  'share_link_auto_revoked',
  'company_created',
  'company_renamed',
  'deletion_requested',
  'company_restored',
] as const

export type ActivityEvent = (typeof ACTIVITY_EVENTS)[number]

export type CauseEvent = 'role_changed' | 'member_removed' | 'member_left'

/** One entry of a company's activity log (`GET /api/activity`). */
export interface ActivityEntry {
  id: string
  at: string
  event: ActivityEvent
  /** `null` only on the rows an operator command (a recovery, a disable) writes. */
  actor: string | null
  /** `null` on a share-link event, which carries `shareLink` instead, and on a company event but `company_created`. */
  target: string | null
  fromRole: Role | null
  toRole: Role | null
  inviter: string | null
  cause: { event: CauseEvent; fromRole: Role | null; toRole: Role | null } | null
  /** The link as it was when the row was written; present on share-link events only. */
  shareLink: { label: string | null; expiresAt: string } | null
  /** The old and new names; present on `company_renamed` only (GRO-136, D4). */
  renamed: { from: string; to: string } | null
}

/**
 * How a page read ended. `unsupported` counts the entries of event types this frontend
 * does not know, which the parser dropped; `forbidden` is the role `403`, `not_found` the
 * membership `404` (the caller was removed, or left).
 */
export type ActivityResult =
  | { status: 'ok'; entries: ActivityEntry[]; unsupported: number; next: string | null }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'unavailable' }

const ROLES: readonly string[] = ['owner', 'admin', 'member', 'viewer']
const CAUSES: readonly string[] = ['role_changed', 'member_removed', 'member_left']
const UNTARGETED: readonly string[] = ['company_renamed', 'deletion_requested', 'company_restored']

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && ROLES.includes(value)
}

function roleOf(value: unknown): Role | null | undefined {
  if (value === null || value === undefined) return null
  return isRole(value) ? value : undefined
}

function emailOf(value: unknown): string | null | undefined {
  if (value === null) return null
  if (typeof value !== 'object' || value === undefined) return undefined
  const { email } = value as Record<string, unknown>
  return typeof email === 'string' ? email : undefined
}

function shareLinkOf(value: unknown): ActivityEntry['shareLink'] | undefined {
  if (value === null || value === undefined) return null
  if (typeof value !== 'object') return undefined
  const { label, expiresAt } = value as Record<string, unknown>
  if (label !== null && label !== undefined && typeof label !== 'string') return undefined
  if (typeof expiresAt !== 'string') return undefined
  return { label: label ?? null, expiresAt }
}

function renamedOf(value: unknown): ActivityEntry['renamed'] | undefined {
  if (value === null || value === undefined) return null
  if (typeof value !== 'object') return undefined
  const { from, to } = value as Record<string, unknown>
  return typeof from === 'string' && typeof to === 'string' ? { from, to } : undefined
}

function causeOf(value: unknown): ActivityEntry['cause'] | undefined {
  if (value === null || value === undefined) return null
  if (typeof value !== 'object') return undefined
  const { event, fromRole, toRole } = value as Record<string, unknown>
  if (typeof event !== 'string' || !CAUSES.includes(event)) return undefined
  const from = roleOf(fromRole)
  const to = roleOf(toRole)
  if (from === undefined || to === undefined) return undefined
  return { event: event as CauseEvent, fromRole: from, toRole: to }
}

/**
 * One entry, `'unsupported'` for a well-formed entry of an event type this frontend does
 * not know, or `null` for a malformed one.
 */
export function entryOf(value: unknown): ActivityEntry | 'unsupported' | null {
  if (typeof value !== 'object' || value === null) return null
  const raw = value as Record<string, unknown>
  if (typeof raw.id !== 'string' || raw.id === '' || typeof raw.at !== 'string') return null
  if (typeof raw.event !== 'string') return null
  if (!(ACTIVITY_EVENTS as readonly string[]).includes(raw.event)) return 'unsupported'
  const actor = emailOf(raw.actor)
  const target = emailOf(raw.target)
  const fromRole = roleOf(raw.fromRole)
  const toRole = roleOf(raw.toRole)
  const cause = causeOf(raw.cause)
  const inviter = raw.inviter === null || raw.inviter === undefined ? null : raw.inviter
  const shareLink = shareLinkOf(raw.shareLink)
  const renamed = renamedOf(raw.renamed)
  if (actor === undefined || target === undefined || shareLink === undefined || renamed === undefined) return null
  // A rename carries both names and no person; it is the only event that carries them.
  if ((raw.event === 'company_renamed') !== (renamed !== null)) return null
  // A share-link event names its link and no person; a company event names a person only
  // when it is `company_created`, whose target is the Owner; every other event names its target.
  if (raw.event.startsWith('share_link_')) {
    if (shareLink === null) return null
  } else if (shareLink !== null) return null
  else if (UNTARGETED.includes(raw.event) ? target !== null : target === null) return null
  if (fromRole === undefined || toRole === undefined || cause === undefined) return null
  if (inviter !== null && typeof inviter !== 'string') return null
  return {
    id: raw.id,
    at: raw.at,
    event: raw.event as ActivityEvent,
    actor,
    target,
    fromRole,
    toRole,
    inviter,
    cause,
    shareLink,
    renamed,
  }
}

/**
 * `GET /api/activity`: one page of the company's log, newest first. Guarded the way
 * `fetchCompanies` is: the generation it was issued under is checked when the response
 * arrives, a 401 is reported as a sign-out, and the request is bound to `authSignal()`.
 */
export async function fetchActivity(
  companyId: string,
  kind: ActivityKind,
  before: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<ActivityResult> {
  const params = new URLSearchParams({ company: companyId })
  if (kind !== 'all') params.set('kind', kind)
  if (before !== null) params.set('before', before)
  const generation = authGeneration()
  let response: Response
  try {
    response = await fetchImpl(`/api/activity?${params}`, { credentials: 'same-origin', signal: authSignal() })
  } catch {
    return { status: 'unavailable' }
  }
  if (generation !== authGeneration()) return { status: 'unavailable' }
  if (response.status === 401) {
    reportSessionExpired()
    return { status: 'unavailable' }
  }
  if (response.status === 403) return { status: 'forbidden' }
  if (response.status === 404) return { status: 'not_found' }
  if (!response.ok) return { status: 'unavailable' }
  try {
    const body = (await response.json()) as { entries?: unknown; next?: unknown }
    if (!Array.isArray(body.entries)) return { status: 'unavailable' }
    if (body.next !== null && typeof body.next !== 'string') return { status: 'unavailable' }
    const entries: ActivityEntry[] = []
    let unsupported = 0
    for (const value of body.entries) {
      const entry = entryOf(value)
      if (entry === null) return { status: 'unavailable' }
      if (entry === 'unsupported') unsupported += 1
      else entries.push(entry)
    }
    return { status: 'ok', entries, unsupported, next: body.next }
  } catch {
    return { status: 'unavailable' }
  }
}
